<template>
  <GpCard
    :active="selected"
    :class="{ 'cursor-pointer ': !selected }"
    @click="$emit('selected', billingProfile.id)"
  >
    <div class="tw-flex tw-gap-4 tw-p-2">
      <p class="tw-w-1/2 tw-whitespace-nowrap">
        <span class="tw-font-bold tw-text-gp-rhino">
          <FontAwesomeIcon :icon="['fas', 'fa-mailbox']" class="tw-mr-2" />
          Address
        </span>
        <br />
        <template v-if="billingProfile.company">
          <strong>{{ billingProfile.company.name }}</strong>
          <br />
          <small>{{ billingProfile.name }}</small>
        </template>
        <template v-else>
          <strong>{{ billingProfile.name }}</strong>
        </template>
        <br />
        <small>
          {{ billingProfile.street }}
          <br />
          {{ billingProfile.city }} {{ billingProfile.postcode }}
          <br />
          {{ billingProfile.countryCode }}
        </small>
      </p>
      <div class="tw-w-1/2">
        <p v-if="billingProfile.company" class="tw-mb-4">
          <span class="tw-font-bold tw-text-gp-rhino">
            <FontAwesomeIcon :icon="['fas', 'fa-percent']" class="tw-mr-2" />
            Tax
          </span>
          <br />
          <span>{{ billingProfile.taxRate }}%</span><br />
        </p>
        <p v-if="isLoadingCreditcards">Loading...</p>
        <p v-else-if="activeCreditCard != null">
          <span class="tw-font-bold tw-text-gp-rhino">
            <FontAwesomeIcon :icon="['fas', 'fa-credit-card']" class="tw-mr-2" />
            <strong>Credit card</strong>
          </span>
          <br />
          <small>{{ activeCreditCardType }}</small> -
          <span class="tw-font-mono tw-text-xs">
            {{ expiresAt }}
          </span>
          <br />
          <span class="tw-font-mono tw-text-xs">
            &#x2022;&#x2022;&#x2022;&#x2022;&nbsp;{{ activeCreditCard.last4Digits }}
          </span>
        </p>
      </div>
    </div>
  </GpCard>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from 'vue';
import { type BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { GpCard, useFaIcons } from '@gportal/uikit';
import { type CreditCard, CreditCardType } from 'gpcore-grpc/gpcore/type/v1/credit_card_pb';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCreditCard, faMailbox, faPercent } from '@fortawesome/pro-solid-svg-icons';
import { convertToDate } from 'gpcore-apikit/lib/date';
import getEnumLabel from 'gpcore-apikit/lib/getEnumLabel';

export default defineComponent({
  components: {
    GpCard,
    FontAwesomeIcon,
  },
  props: {
    selected: {
      type: Boolean,
      default: false,
    },
    deleteable: {
      type: Boolean,
      default: false,
    },
    billingProfile: {
      type: Object as PropType<BillingProfile>,
      required: true,
    },
  },
  setup() {
    useFaIcons({ faCreditCard, faPercent, faMailbox });

    const creditCards = ref<Array<CreditCard>>([]);
    const activeCreditCard = computed(() => creditCards.value.find((cc) => cc.active));
    const activeCreditCardType = computed(() => {
      if (activeCreditCard.value.type === CreditCardType.UNSPECIFIED) {
        return 'UNKNOWN';
      }

      return getEnumLabel(CreditCardType, activeCreditCard.value.type) || 'UNKNOWN';
    });

    const isLoadingCreditcards = ref(false);
    const loadingCreditcardsError = ref('');

    const dateFormat = new Intl.DateTimeFormat('en-GB', {
      month: '2-digit',
      year: '2-digit',
    });

    const expiresAt = computed(() => {
      if (activeCreditCard.value == null) {
        return null;
      }

      return dateFormat.format(convertToDate(activeCreditCard.value.expiresAt));
    });

    return {
      isLoadingCreditcards,
      loadingCreditcardsError,
      activeCreditCard,
      activeCreditCardType,
      expiresAt,
    };
  },
});
</script>
